import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@web/components/ui/form';

import { CircleX } from 'lucide-react';
import { UpdateFormData, updateFormSchema } from '@shared/types';
import { Button } from '@web/components/ui/button';
import { trpc } from '@web/lib/trpc';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@web/components/ui/select';
import { ContactWithDetails } from '@shared/types/ContactWithDetails';
import { InputAnimatedLabel } from '@web/components/ui/input-animated-label';
import { useContext } from 'react';
import { ContactListContext } from '../contexts';
import { ButtonLoading } from '@web/components/ui/button-loading';

export function UpdateContactForm({
  contactDetails,
  setIsDialogOpen,
}: {
  contactDetails: ContactWithDetails;
  setIsDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const refetchContacts = useContext(ContactListContext);
  const mutation = trpc.contact.updateContactWithDetails.useMutation({
    onSuccess: () => {
      console.log('successs updating the contact!!');
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      void refetchContacts();
    },
  });
  function onSubmit(values: UpdateFormData) {
    // Do something with the form values.
    // ✅ This will be type-safe and validated.
    console.log('sending the update request...');
    mutation.mutate({
      contactId: contactDetails.id,
      contactData: values,
    });
  }

  const form = useForm<UpdateFormData>({
    resolver: zodResolver(updateFormSchema),
    defaultValues: {
      firstName: contactDetails.firstName ?? '',
      lastName: contactDetails.lastName ?? '',
      email: contactDetails.email ?? '',
      phoneNumber: contactDetails.phoneNumber,

      //details
      sex: contactDetails.details.sex ?? 'female',
      address: contactDetails.details.address ?? '',
      country: contactDetails.details.country ?? '',
      city: contactDetails.details.city ?? '',
      state: contactDetails.details.state ?? '',
      zipCode: contactDetails.details.zipCode ?? '',
      maritalStatus: contactDetails.details.maritalStatus ?? 'single',
      countryCode: contactDetails.countryCode ?? 1,
    },
  });

  return (
    <Form {...form}>
      <form
        onSubmit={(e) => {
          void form.handleSubmit(onSubmit)(e);
        }}
        className="space-y-8"
      >
        <button
          aria-label="Close"
          onClick={() => {
            setIsDialogOpen(false);
          }}
          className="absolute right-5 top-6 rounded-full focus:outline-none focus:ring-2 focus:ring-gray-500"
        >
          <CircleX />
        </button>

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <InputAnimatedLabel {...field} label="Email" />
              </FormControl>
              <FormDescription></FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="firstName"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <InputAnimatedLabel {...field} label="First Name" />
              </FormControl>
              <FormDescription></FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="lastName"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <InputAnimatedLabel {...field} label="Last Name" />
              </FormControl>
              <FormDescription></FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="phoneNumber"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <InputAnimatedLabel {...field} label="Phone Number" />
              </FormControl>
              <FormDescription></FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="sex"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-black">Sex</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select sex" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="">
                  <SelectItem value="female">Female</SelectItem>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="transgender">Transgender</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
                <FormDescription></FormDescription>
                <FormMessage />
              </Select>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="maritalStatus"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-black">Marital Status</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select marital status" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="">
                  <SelectItem value="single">Single</SelectItem>
                  <SelectItem value="divorced">Divorced</SelectItem>
                  <SelectItem value="married">Married</SelectItem>
                  <SelectItem value="widowed">Widowed</SelectItem>
                </SelectContent>
                <FormDescription></FormDescription>
                <FormMessage />
              </Select>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="address"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <InputAnimatedLabel {...field} label="Address" />
              </FormControl>
              <FormDescription></FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="country"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <InputAnimatedLabel {...field} label="Country" />
              </FormControl>
              <FormDescription></FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="city"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <InputAnimatedLabel {...field} label="City" />
              </FormControl>
              <FormDescription></FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="state"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <InputAnimatedLabel {...field} label="State" />
              </FormControl>
              <FormDescription></FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="zipCode"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <InputAnimatedLabel {...field} label="Zip code" />
              </FormControl>
              <FormDescription></FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="countryCode"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <InputAnimatedLabel {...field} label="Country code" />
              </FormControl>
              <FormDescription></FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="mt-auto">
          <div className="flex items-center justify-end gap-4">
            <Button
              variant="outline"
              onClick={() => {
                setIsDialogOpen(false);
              }}
            >
              Cancel
            </Button>
            <ButtonLoading
              type="submit"
              isLoading={mutation.isPending}
              disabled={mutation.isPending}
              variant="default"
              onClick={() => undefined}
            >
              Update
            </ButtonLoading>
          </div>
        </div>
      </form>
    </Form>
  );
}
